// /en/visa/documents — Wave 66: EN hub of the six visa document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/visa/documents";
const URL = `${SITE}${PATH}`;
const TITLE = "Visa supporting documents that need translation — 6 families | NYC";
const DESC =
  "The six families of visa supporting documents that need certified translation and legalisation, with the translation–consular–embassy order, preparation checklists and FAQs.";

export const Route = createFileRoute("/en/visa/documents/")({
  loader: async () => {
    const { VISA_DOC_GROUPS } = await import("@/data/visa-doc-groups");
    return {
      __d: { VISA_DOC_GROUPS },
      groups: VISA_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleEn,
        faqs: g.faqsEn.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "en") },
      { name: "description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "en") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: `${SITE}/visa/documents` },
      { rel: "alternate", hrefLang: "x-default", href: `${SITE}/visa/documents` },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Visa supporting-document families",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: VisaDocumentsEnPage,
});

function VisaDocumentsEnPage() {
  const { __d: { VISA_DOC_GROUPS } } = Route.useLoaderData() as { __d: typeof import("@/data/visa-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">
        <Link to="/en/visa" className="hover:underline">
          Visa services
        </Link>{" "}
        / Visa supporting documents
      </nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">
        Visa supporting documents that need translation — six families
      </h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        Thai-language documents filed with an embassy, a consulate or an immigration office usually need
        a translation with a signed certificate of accuracy, and in many cases legalisation by the
        Department of Consular Affairs before the destination embassy will accept them. This page groups
        the documents by how they are used, with the order of steps and a checklist for each family.
        Approval is always at the discretion of the immigration or consular authority.
      </p>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {VISA_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link
              to="/en/visa/documents/$group"
              params={{ group: g.slug }}
              className="text-primary hover:underline"
            >
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>

      {VISA_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyEn.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/en/visa/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            Read the full {g.titleEn} guide →
          </Link>
        </section>
      ))}
          <ServiceAuthorityBlock topic="visa" locale="en" />
    </main>
  );
}
